import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../../components/commons/Sidebar";
import Swal from "sweetalert2";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ImageUploader from "../../components/admin/ImageUploader";
import { getAdminPosts, createPost, updatePost, deletePost, uploadImage } from "../../api/blogAPI";

const BASE = process.env.REACT_APP_EXPRESS_API_BASE_URL || "http://localhost:3001";

const EMPTY_FORM = {
  title: "",
  slug: "",
  cover_img: "",
  excerpt: "",
  content: "",
  status: "draft",
};

const slugify = (str) =>
  str.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

let _imageHandlerRef = null;

const modules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["blockquote", "code-block"],
      ["link", "image"],
      ["clean"],
    ],
    handlers: {
      image: () => _imageHandlerRef && _imageHandlerRef(),
    },
  },
};

const BlogAdmin = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | editor
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const quillRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getAdminPosts();
      setPosts(Array.isArray(data) ? data : []);
    } catch {
      Swal.fire({ icon: "error", title: "Failed to load posts" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setView("editor");
  };

  const openEdit = (post) => {
    setForm({
      title: post.title ?? "",
      slug: post.slug ?? "",
      cover_img: post.cover_img ?? "",
      excerpt: post.excerpt ?? "",
      content: post.content ?? "",
      status: post.status ?? "draft",
    });
    setEditId(post.id);
    setView("editor");
  };

  const handleTitleChange = (val) => {
    setForm((f) => ({
      ...f,
      title: val,
      slug: editId ? f.slug : slugify(val),
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) return Swal.fire({ icon: "warning", text: "Title is required." });
    setSaving(true);
    try {
      if (editId) {
        await updatePost(editId, form);
      } else {
        await createPost(form);
      }
      await load();
      setView("list");
      Swal.fire({ icon: "success", title: "Saved!", timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: "error", text: err.response?.data?.message || "Failed to save." });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete this post?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then(async ({ isConfirmed }) => {
      if (!isConfirmed) return;
      try {
        await deletePost(id);
        await load();
        Swal.fire({ icon: "success", title: "Deleted!", timer: 1000, showConfirmButton: false });
      } catch {
        Swal.fire({ icon: "error", text: "Failed to delete." });
      }
    });
  };

  _imageHandlerRef = () => {
    const editor = quillRef.current?.getEditor();
    const savedRange = editor?.getSelection();
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      try {
        const url = await uploadImage(file);
        const insertIndex = savedRange ? savedRange.index : (editor?.getLength() ?? 0) - 1;
        editor?.insertEmbed(insertIndex, "image", url);
        editor?.setSelection(insertIndex + 1);
      } catch (err) {
        Swal.fire({ icon: "error", text: "Failed to upload image: " + (err?.response?.data?.error || err.message || "unknown") });
      }
    };
  };

  if (view === "editor") {
    return (
      <div className="flex flex-col md:flex-row min-h-screen bg-gray-900">
        <Sidebar />
        <div className="flex-1 p-6 lg:p-10 overflow-auto">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <button onClick={() => setView("list")} className="text-zinc-400 hover:text-white transition-colors text-sm">
                ← Back
              </button>
              <h2 className="text-2xl font-black text-white">{editId ? "Edit Post" : "New Post"}</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest block mb-1">Title</label>
                <input
                  value={form.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Post title..."
                />
              </div>

              <div>
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest block mb-1">Slug</label>
                <input
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="url-slug-post"
                />
              </div>

              <ImageUploader
                label="Cover Image"
                value={form.cover_img}
                onChange={(v) => setForm((f) => ({ ...f, cover_img: v }))}
              />

              <div>
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest block mb-1">Excerpt</label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
                  rows={2}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  placeholder="Short description..."
                />
              </div>

              <div>
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest block mb-1">Content</label>
                <div className="quill-dark rounded-xl overflow-hidden border border-zinc-700">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={form.content}
                    onChange={(val) => setForm((f) => ({ ...f, content: val }))}
                    modules={modules}
                  />
                </div>
                <style>{`
                  .quill-dark .ql-toolbar { background: #27272a; border-color: #3f3f46 !important; border-radius: 12px 12px 0 0; }
                  .quill-dark .ql-toolbar .ql-stroke { stroke: #a1a1aa; }
                  .quill-dark .ql-toolbar .ql-fill { fill: #a1a1aa; }
                  .quill-dark .ql-toolbar .ql-picker { color: #a1a1aa; }
                  .quill-dark .ql-toolbar button:hover .ql-stroke,
                  .quill-dark .ql-toolbar button.ql-active .ql-stroke { stroke: #fff; }
                  .quill-dark .ql-toolbar button:hover .ql-fill,
                  .quill-dark .ql-toolbar button.ql-active .ql-fill { fill: #fff; }
                  .quill-dark .ql-toolbar .ql-picker-label:hover,
                  .quill-dark .ql-toolbar .ql-picker-label.ql-active { color: #fff; }
                  .quill-dark .ql-container { background: #18181b; border-color: #3f3f46 !important; border-radius: 0 0 12px 12px; min-height: 300px; }
                  .quill-dark .ql-editor { color: #f4f4f5; min-height: 300px; font-size: 15px; line-height: 1.7; }
                  .quill-dark .ql-editor.ql-blank::before { color: #52525b; font-style: italic; }
                  .quill-dark .ql-picker-options { background: #27272a; border-color: #3f3f46; }
                  .quill-dark .ql-picker-item { color: #a1a1aa; }
                  .quill-dark .ql-picker-item:hover { color: #fff; }
                `}</style>
              </div>

              <div className="flex items-center gap-4">
                <div>
                  <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest block mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className="bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={() => setView("list")}
                  className="px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-xl transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-900">
      <Sidebar />
      <div className="flex-1 p-6 lg:p-10 overflow-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-indigo-400">Management</p>
            <h2 className="text-3xl font-black text-white">BLOG POSTS</h2>
          </div>
          <button
            onClick={openNew}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl transition-colors"
          >
            + New Post
          </button>
        </div>

        {loading ? (
          <p className="text-zinc-500">Loading...</p>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-zinc-600">No blog posts yet.</div>
        ) : (
          <div className="space-y-3">
            {posts.map((p) => (
              <div key={p.id} className="bg-zinc-900/40 border border-zinc-800 rounded-2xl p-5 flex items-start gap-4">
                {p.cover_img && (
                  <img src={p.cover_img.startsWith("/") ? `${BASE}${p.cover_img}` : p.cover_img} alt="" className="w-16 h-16 object-cover rounded-xl flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-700 text-zinc-400"}`}>
                      {p.status}
                    </span>
                    <span className="text-[10px] text-zinc-600">{p.view_count ?? 0} views</span>
                  </div>
                  <h3 className="text-white font-bold truncate">{p.title}</h3>
                  <p className="text-zinc-500 text-xs mt-0.5 truncate">{p.slug}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => openEdit(p)}
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-indigo-600 text-white text-xs font-bold rounded-lg transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)}
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-red-600 text-white text-xs font-bold rounded-lg transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogAdmin;
